import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import {
  selectFilteredVirtualCards,
  selectVirtualCardsFullPaginatedData,
} from 'src/app/VirtualCardsNeedsApproval/redux/virtualCardsNeedsApproval.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import PendingRequestsBox from 'src/components/PendingRequestsBox/PendingRequestsBox'
import { VirtualCardsFiltersBody } from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useBackgroundDispatch } from 'src/hooks'
import { orgCardApprovalRulesQueries } from 'src/queries/organizations/cardApprovalRules/cardApprovalRules.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { getVirtualCards } from 'src/redux/actions/data/virtualCards.actions'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const VirtualCardsNeedsApproval: React.FC = () => {
  const virtualCardsFullPaginatedData = useSelector(
    selectVirtualCardsFullPaginatedData,
  )
  const filteredVirtualCards = useSelector(selectFilteredVirtualCards)
  const appName = useSelector(selectAppName)
  const dispatch = useBackgroundDispatch()
  const user = useCurrentUser()
  const cardApprovalRulesQuery = useQuery(
    orgCardApprovalRulesQueries.detail(user?.organizationId),
  )
  const cardApprovalRules = cardApprovalRulesQuery.data
  const cardApprovalRulesFetched = cardApprovalRulesQuery.isFetched
  const isUserOrganizationCardApprover =
    cardApprovalRules?.approverId === user?.id
  const { results: statisticsResults } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.VirtualCardsCardholderPendingRequestOrApproverRuleApprovalCount,
      StatisticsKeys.VirtualCardsPendingRequestCardholderMeCount,
    ] as const,
  })
  const virtualCardsPendingRequestCardholderMeCount =
    statisticsResults[StatisticsKeys.VirtualCardsPendingRequestCardholderMeCount]
      .data ?? 0
  const virtualCardsCardholderPendingRequestOrApproverRuleApprovalCountStatistics =
    statisticsResults[
      StatisticsKeys.VirtualCardsCardholderPendingRequestOrApproverRuleApprovalCount
    ].data ?? 0

  const fetchVirtualCards = (
    filtersBody: VirtualCardsFiltersBody,
    page?: number,
  ): void => {
    if (cardApprovalRulesFetched) {
      dispatch(
        getVirtualCards({
          count: 10,
          page: page,
          cardholder: 'me',
          pendingRequest: true,
          ...filtersBody,
        }),
      )
    }
  }
  const fetchVirtualCardsForCardApprover = (
    filtersBody: VirtualCardsFiltersBody,
    page?: number,
  ): void => {
    dispatch(
      getVirtualCards({
        count: 10,
        page: page,
        cardholderPendingRequestOrApproverRuleApproval: 'me',
        ...filtersBody,
      }),
    )
  }

  const infiniteScrollProps = {
    ...virtualCardsFullPaginatedData,
    fetch: fetchVirtualCards,
  }

  const cardApproverScrollProps = {
    ...virtualCardsFullPaginatedData,
    fetch: fetchVirtualCardsForCardApprover,
  }

  return (
    <>
      <Helmet>
        <title>{`Requests | ${appName}`}</title>
        <meta name="description" content={`Requests page of ${appName}`} />
      </Helmet>
      <BackgroundPageContent noPadding>
        <PendingRequestsBox
          virtualCards={filteredVirtualCards}
          infiniteScrollProps={infiniteScrollProps}
          isUserCardApprover={isUserOrganizationCardApprover}
          cardApprovalRulesFetched={cardApprovalRulesFetched}
          cardApproverScrollProps={cardApproverScrollProps}
          showLoadingPage={
            !statisticsResults[
              StatisticsKeys
                .VirtualCardsCardholderPendingRequestOrApproverRuleApprovalCount
            ].isSuccess ||
            !statisticsResults[
              StatisticsKeys.VirtualCardsPendingRequestCardholderMeCount
            ].isSuccess
          }
          totalCount={
            isUserOrganizationCardApprover
              ? virtualCardsCardholderPendingRequestOrApproverRuleApprovalCountStatistics
              : virtualCardsPendingRequestCardholderMeCount
          }
        />
      </BackgroundPageContent>
    </>
  )
}

export default VirtualCardsNeedsApproval
